import { ImpersonationBanner, Navbar, NotificationAlertsBanner, PrivateRoute, Spinner } from "@/components";
import { SubscriptionBanner } from "@/components/SubscriptionBanner";
import { routeErrorElement } from "@/components/ErrorFallback";
import { App as AntApp, Button, Result } from "antd";
import axios from "axios";

import { lazy, Suspense, useEffect } from "react";
import { createBrowserRouter, Navigate, Outlet, RouterProvider, ScrollRestoration, useLocation } from "react-router";
import { useMyUser } from "./actions/user.actions";
import useAxiosInterceptor from "./lib/Interceptor";
import { useIsImpersonating } from "./lib/isImpersonating";
import LandingPage from "./pages/LandingPage";
import LandingFooter from "./pages/LandingPage/components/LandingFooter";
import { useAuthStore } from "./states";
import { AuthStoreType } from "./states/auth";
import { useCrmBranding } from "./components/CrmBranding";

// Logged-out routes
const AboutPage = lazy(() => import("@/pages/LandingPage/AboutPage"));
const FeaturesPage = lazy(() => import("@/pages/LandingPage/FeaturesPage"));
const ProductTourPage = lazy(() => import("@/pages/LandingPage/ProductTourPage"));
const SecurityTrustPage = lazy(() => import("@/pages/LandingPage/SecurityTrustPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/StaticPages/PrivacyPolicyPage"));
const TermsConditionsPage = lazy(() => import("@/pages/StaticPages/TermsConditionsPage"));

// Logged-in routes
const HomePage = lazy(() => import("@/pages/HomePage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const AssistantPage = lazy(() => import("@/pages/AssistantPage"));
const TaxationReportPage = lazy(() => import("@/pages/ReportsPage/TaxationReportPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const ReportViewerPage = lazy(() => import("@/pages/ReportsPage/ReportViewerPage"));
const ReportBuilderPage = lazy(() => import("@/pages/ReportsPage/ReportBuilderPage"));
const ProfilePage = lazy(() => import("@/pages/AuthPages/ProfilePage"));
const OrganisationPage = lazy(() => import("@/pages/OrgPage"));
const LoginPage = lazy(() => import("@/pages/AuthPages/LoginPage"));
const ListLeadsPage = lazy(() => import("@/pages/LeadsPage/ListLeadsPage"));
const CalendarViewPage = lazy(() => import("@/pages/LeadsPage/CalendarViewPage"));
const EditQueryPage = lazy(() => import("@/pages/LeadsPage/EditQueryPage"));
const ViewQueryPage = lazy(() => import("@/pages/LeadsPage/ViewQueryPage"));
const ListItinerariesPage = lazy(() => import("@/pages/ItinerariesPage/ListItinerariesPage"));
// PHASE4_REMOVE_BOOKING_UI: legacy booking UI routes are blocked while booking code remains for compatibility.
// const ListBookingsPage = lazy(() => import("@/pages/BookingsPage/ListBookingsPage"));
// const EditBookingsPage = lazy(() => import("@/pages/BookingsPage/EditBookingsPage"));
const ListPaymentsPage = lazy(() => import("@/pages/PaymentsPage/ListPaymentsPage"));
const EditPaymentSchedulePage = lazy(() => import("@/pages/PaymentsPage/EditPaymentSchedulePage"));
const ViewLeadPaymentsPage = lazy(() => import("@/pages/PaymentsPage/ViewLeadPaymentsPage"));
const NewItineraryPage = lazy(() => import("@/pages/ItineraryPage/NewItineraryPage"));
const ConfirmItineraryPage = lazy(() => import("@/pages/ItineraryPage/ConfirmItineraryPage"));
const ItineraryTemplatesPage = lazy(() => import("@/pages/ItineraryTemplatesPage"));
const ItineraryTemplateEditorPage = lazy(() => import("@/pages/ItineraryTemplatesPage/CreateTemplateModal"));
const OrganisationPageOrg = lazy(() => import("@/pages/OrgPage/OrganisationPageOrg"));
const ListUsersPage = lazy(() => import("@/pages/OrgPage/Users/ListUsersPage"));
const EditUserPage = lazy(() => import("@/pages/OrgPage/Users/EditUserPage"));
const ListTeamsPage = lazy(() => import("@/pages/OrgPage/Teams/ListTeamsPage"));
const EditTeamPage = lazy(() => import("@/pages/OrgPage/Teams/EditTeamPage"));
const ListHotelsRepositoryPage = lazy(() => import("@/pages/OrgPage/HotelsRepository/ListHotelsRepositoryPage"));
const EditHotelRepositoryPage = lazy(() => import("@/pages/OrgPage/HotelsRepository/EditHotelRepositoryPage"));
const ListActivitiesRepositoryPage = lazy(
  () => import("@/pages/OrgPage/ActivitiesRepository/ListActivitiesRepositoryPage"),
);
const EditActivityRepositoryPage = lazy(
  () => import("@/pages/OrgPage/ActivitiesRepository/EditActivityRepositoryPage"),
);
const ListTransportsRepositoryPage = lazy(
  () => import("@/pages/OrgPage/TransportsRepository/ListTransportsRepositoryPage"),
);
const EditTransportRepositoryPage = lazy(
  () => import("@/pages/OrgPage/TransportsRepository/EditTransportRepositoryPage"),
);
const WhatsAppAccountsPage = lazy(() => import("@/pages/OrgPage/WhatsApp"));
const ListMailboxesPage = lazy(() => import("@/pages/OrgPage/Mailboxes/ListMailboxesPage"));
const EditMailboxesPage = lazy(() => import("@/pages/OrgPage/Mailboxes/EditMailboxesPage"));
const MailboxInboxPage = lazy(() => import("@/pages/MailboxPage"));
const WhatsAppPage = lazy(() => import("@/pages/WhatsAppPage"));
const ListQuerySourcesPage = lazy(() => import("@/pages/OrgPage/QuerySources/ListQuerySourcesPage"));
const EditQuerySourcePage = lazy(() => import("@/pages/OrgPage/QuerySources/EditQuerySourcePage"));
const ListSuppliersPage = lazy(() => import("@/pages/OrgPage/Suppliers/ListSuppliersPage"));
const EditSupplierPage = lazy(() => import("@/pages/OrgPage/Suppliers/EditSupplierPage"));
const ListAgenciesPage = lazy(() => import("@/pages/OrgPage/Agencies/ListAgenciesPage"));
const ListContactsPage = lazy(() => import("@/pages/Contacts"));
const CampaignsPage = lazy(() => import("@/pages/CampaignsPage"));
const CampaignDetailPage = lazy(() => import("@/pages/CampaignsPage/CampaignDetailPage"));
const AutoAssignmentRulesPage = lazy(() => import("@/pages/OrgPage/AutoAssignmentRules"));
const CurrencyConversionsPage = lazy(() => import("@/pages/OrgPage/CurrencyConversions"));
const NationalityGroupsPage = lazy(() => import("@/pages/OrgPage/NationalityGroups"));
const ItineraryCustomizationPage = lazy(() => import("@/pages/OrgPage/ItineraryCustomization"));
const PreferencesPage = lazy(() => import("@/pages/OrgPage/Preferences"));
const CustomFieldsPage = lazy(() => import("@/pages/OrgPage/CustomFields"));
const IntegrationsPage = lazy(() => import("@/pages/OrgPage/Integrations"));
const AISettingsPage = lazy(() => import("@/pages/OrgPage/AISettings"));
const AutomatedEmailsPage = lazy(() => import("@/pages/OrgPage/AutomatedEmails"));
const AgentPortalSettingsPage = lazy(() => import("@/pages/OrgPage/AgentPortalPage"));
const AgencyUsersPage = lazy(() => import("@/pages/OrgPage/Agencies/AgencyUsersPage"));
const AdminTenantListPage = lazy(() => import("@/pages/AdminPage/tenantlist"));
const SuperadminDashboardPage = lazy(() => import("@/pages/AdminPage/dashboard"));
const AdminEditTenantPage = lazy(() => import("@/pages/AdminPage/tenantlist/EditTenantPage"));
const AdminAIKeysPage = lazy(() => import("@/pages/AdminPage/aikeys"));
const AdminLocationListPage = lazy(() => import("@/pages/AdminPage/locationlist"));
const AdminTenantOrganisationPage = lazy(() => import("@/pages/AdminPage/tenantorganisation"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const CrmWhiteLabelPage = lazy(() => import("@/pages/AdminPage/CrmWhiteLabelPage"));

/** Replaces platform public pages with the tenant's authenticated entry route. */
const PublicPage = ({ children }: { children: React.ReactNode }) => {
  const { branding } = useCrmBranding();
  const token = useAuthStore((state) => state.token);
  return branding ? <Navigate to={token ? "/dashboard" : "/login"} replace /> : children;
};

const CrmHome = () => {
  const { branding } = useCrmBranding();
  return branding ? <Navigate to="/dashboard" replace /> : <HomePage />;
};

const LoggedOutLayout = () => {
  const { branding } = useCrmBranding();
  const location = useLocation();
  const isLandingRoot = location.pathname === "/";
  const isSelfContainedLandingPage =
    isLandingRoot ||
    location.pathname === "/security" ||
    location.pathname === "/features" ||
    location.pathname === "/about" ||
    location.pathname === "/privacy-policy" ||
    location.pathname === "/terms-conditions";

  if (branding) return <Navigate to="/login" replace />;
  return (
    <main className="min-h-screen bg-[#f5f5f3]">
      <div className={isSelfContainedLandingPage ? "" : "mt-12"}>
        <ScrollRestoration />
        <Outlet />
      </div>
      {!isSelfContainedLandingPage ? <LandingFooter /> : null}
    </main>
  );
};
const Layout = () => {
  const isImpersonating = useIsImpersonating();
  return (
    <PrivateRoute>
      <Suspense
        fallback={
          <div className="h-[50vh] flex justify-center items-center">
            <Spinner />
          </div>
        }
      >
        <Navbar />
        <NotificationAlertsBanner />
        <ImpersonationBanner />
        <div className={`pb-6 ${isImpersonating ? "mt-32" : "mt-16"}`}>
          <SubscriptionBanner />
          <ScrollRestoration />
          <Outlet />
        </div>
      </Suspense>
    </PrivateRoute>
  );
};

const NoMatch = () => {
  const { branding } = useCrmBranding();
  const token = useAuthStore((state) => state.token);
  if (branding && !token) return <Navigate to="/login" replace />;
  return (
    <div className="h-96 content-center text-center pt-20">
      <Result
        status="404"
        title="404"
        subTitle="Sorry, the page you visited does not exist."
        extra={
          <Button
            type="primary"
            onClick={() => {
              window.location.href = "/";
            }}
          >
            Back Home
          </Button>
        }
      />
    </div>
  );
};

const loggedInRouter = createBrowserRouter([
  {
    path: "/privacy-policy",
    errorElement: routeErrorElement,
    element: <PublicPage><PrivacyPolicyPage /></PublicPage>,
  },
  {
    path: "/terms-conditions",
    errorElement: routeErrorElement,
    element: <PublicPage><TermsConditionsPage /></PublicPage>,
  },
  {
    path: "/",
    errorElement: routeErrorElement,
    element: <Layout />,
    children: [
      { index: true, element: <CrmHome /> },
      { path: "login", element: <Navigate to="/" replace /> },
      ...["about", "features", "product-tour", "security", "privacy-policy.html", "terms-conditions.html"].map((path) => ({ path, element: <PublicPage><NoMatch /></PublicPage> })),
      { path: "dashboard", element: <DashboardPage /> },
      { path: "assistant", element: <AssistantPage /> },
      { path: "assistant/:threadId", element: <AssistantPage /> },
      {
        path: "reports",
        children: [
          { index: true, element: <ReportsPage /> },
          { path: "new", element: <ReportBuilderPage /> },
          { path: "preset/taxation-report", element: <TaxationReportPage /> },
          { path: "preset/:key", element: <ReportViewerPage source="preset" /> },
          { path: "saved/:id", element: <ReportViewerPage source="saved" /> },
          { path: "saved/:id/edit", element: <ReportBuilderPage /> },
        ],
      },
      { path: "profile", element: <ProfilePage /> },
      { path: "notifications", element: <NotificationsPage /> },
      {
        path: "leads",
        children: [
          { index: true, element: <ListLeadsPage /> },
          { path: "calendar", element: <CalendarViewPage /> },
          { path: "new", element: <EditQueryPage /> },
          { path: "edit/:id", element: <EditQueryPage /> },
          { path: ":id", element: <ViewQueryPage /> },
          { path: ":leadId/itinerary", element: <NewItineraryPage /> },
          { path: ":leadId/itinerary/:id", element: <NewItineraryPage /> },
          { path: ":leadId/itinerary/:id/confirm", element: <ConfirmItineraryPage /> },
          // PHASE4_REMOVE_BOOKING_UI: old operations page used EditBookingsPage; use the confirmed itinerary page instead.
          // { path: ":leadId/itinerary/:itineraryId/operations", element: <EditBookingsPage /> },
        ],
      },
      {
        path: "mailbox",
        element: <MailboxInboxPage />,
      },
      { path: "whatsapp", element: <WhatsAppPage /> },
      { path: "itineraries", element: <ListItinerariesPage /> },
      {
        path: "itinerary-templates",
        children: [
          { index: true, element: <ItineraryTemplatesPage /> },
          { path: "new", element: <ItineraryTemplateEditorPage /> },
          { path: ":id/edit", element: <ItineraryTemplateEditorPage /> },
        ],
      },
      // PHASE4_REMOVE_BOOKING_UI: old booking list/edit UI is blocked; operations now live on itinerary pages.
      // {
      //   path: "bookings",
      //   children: [
      //     { index: true, element: <ListBookingsPage /> },
      //     { path: ":id", element: <EditBookingsPage /> },
      //   ],
      // },
      {
        path: "payments",
        children: [
          { index: true, element: <ListPaymentsPage /> },
          { path: ":leadId", element: <ViewLeadPaymentsPage /> },
          { path: ":leadId/schedules/:scheduleId", element: <EditPaymentSchedulePage /> },
        ],
      },
      {
        path: "campaigns",
        element: <CampaignsPage />,
      },
      {
        path: "campaign/:id",
        element: <CampaignDetailPage />,
      },
      {
        path: "contacts",
        element: <ListContactsPage />,
      },
      {
        path: "org",
        element: <OrganisationPage />,
        children: [
          { index: true, element: <OrganisationPageOrg /> },
          { path: "organisation", element: <OrganisationPageOrg /> },
          { path: "auto-assignment-rules", element: <AutoAssignmentRulesPage /> },
          { path: "currency-rates", element: <CurrencyConversionsPage /> },
          { path: "nationality-groups", element: <NationalityGroupsPage /> },
          { path: "preferences", element: <PreferencesPage /> },
          { path: "itinerary-customization", element: <ItineraryCustomizationPage /> },
          { path: "custom-fields", element: <CustomFieldsPage /> },
          { path: "integrations", element: <IntegrationsPage /> },
          { path: "ai-settings", element: <AISettingsPage /> },
          { path: "automated-emails", element: <AutomatedEmailsPage /> },
          { path: "agent-portal", element: <AgentPortalSettingsPage /> },
          { path: "agencies", element: <ListAgenciesPage /> },
          { path: "agencies/:id/users", element: <AgencyUsersPage /> },
          { path: "users", element: <ListUsersPage /> },
          { path: "users/new", element: <EditUserPage /> },
          { path: "users/:id", element: <EditUserPage /> },
          { path: "teams", element: <ListTeamsPage /> },
          { path: "teams/new", element: <EditTeamPage /> },
          { path: "teams/:id", element: <EditTeamPage /> },
          { path: "hotels-repository", element: <ListHotelsRepositoryPage /> },
          {
            path: "hotels-repository/new",
            element: <EditHotelRepositoryPage />,
          },
          {
            path: "hotels-repository/:id",
            element: <EditHotelRepositoryPage />,
          },
          {
            path: "activities-repository",
            element: <ListActivitiesRepositoryPage />,
          },
          {
            path: "activities-repository/new",
            element: <EditActivityRepositoryPage />,
          },
          {
            path: "activities-repository/:id",
            element: <EditActivityRepositoryPage />,
          },
          {
            path: "transports-repository",
            element: <ListTransportsRepositoryPage />,
          },
          {
            path: "transports-repository/new",
            element: <EditTransportRepositoryPage />,
          },
          {
            path: "transports-repository/:id",
            element: <EditTransportRepositoryPage />,
          },
          {
            path: "whatsapp",
            element: <WhatsAppAccountsPage />,
          },
          {
            path: "mailboxes",
            element: <ListMailboxesPage />,
          },
          {
            path: "mailboxes/new",
            element: <EditMailboxesPage />,
          },
          {
            path: "mailboxes/:id",
            element: <EditMailboxesPage />,
          },
          {
            path: "query-sources",
            element: <ListQuerySourcesPage />,
          },
          {
            path: "query-source/new",
            element: <EditQuerySourcePage />,
          },
          {
            path: "query-source/:id",
            element: <EditQuerySourcePage />,
          },
          {
            path: "suppliers",
            element: <ListSuppliersPage />,
          },
          {
            path: "supplier/new",
            element: <EditSupplierPage />,
          },
          {
            path: "supplier/:id",
            element: <EditSupplierPage />,
          },
        ],
      },
      { path: "*", element: <NoMatch /> },
    ],
  },
]);

const superAdminRouter = createBrowserRouter([
  {
    path: "/privacy-policy",
    errorElement: routeErrorElement,
    element: <PrivacyPolicyPage />,
  },
  {
    path: "/terms-conditions",
    errorElement: routeErrorElement,
    element: <TermsConditionsPage />,
  },
  {
    path: "/",
    errorElement: routeErrorElement,
    element: <Layout />,
    children: [
      { index: true, element: <Navigate to="/admin/dashboard" replace /> },
      { path: "admin/dashboard", element: <SuperadminDashboardPage /> },
      { path: "profile", element: <ProfilePage /> },
      { path: "admin/tenants", element: <AdminTenantListPage /> },
      { path: "admin/tenants/:tenantId/edit", element: <AdminEditTenantPage /> },
      { path: "admin/users", element: <Navigate to="/admin/tenants" replace /> },
      { path: "admin/locations", element: <AdminLocationListPage /> },
      { path: "admin/ai-keys", element: <AdminAIKeysPage /> },
      {
        path: "admin/tenants/:tenantId/org",
        element: <AdminTenantOrganisationPage />,
        children: [
          { path: "crm-white-label", element: <CrmWhiteLabelPage /> },
          { index: true, element: <OrganisationPageOrg /> },
          { path: "organisation", element: <OrganisationPageOrg /> },
          { path: "currency-rates", element: <CurrencyConversionsPage /> },
          { path: "nationality-groups", element: <NationalityGroupsPage /> },
          { path: "agent-portal", element: <AgentPortalSettingsPage /> },
          { path: "agencies", element: <ListAgenciesPage /> },
          { path: "agencies/:id/users", element: <AgencyUsersPage /> },
          { path: "auto-assignment-rules", element: <AutoAssignmentRulesPage /> },
          { path: "preferences", element: <PreferencesPage /> },
          { path: "itinerary-customization", element: <ItineraryCustomizationPage /> },
          { path: "custom-fields", element: <CustomFieldsPage /> },
          { path: "integrations", element: <IntegrationsPage /> },
          { path: "ai-settings", element: <AISettingsPage /> },
          { path: "automated-emails", element: <AutomatedEmailsPage /> },
          { path: "users", element: <ListUsersPage /> },
          { path: "users/new", element: <EditUserPage /> },
          { path: "users/:id", element: <EditUserPage /> },
          { path: "teams", element: <ListTeamsPage /> },
          { path: "teams/new", element: <EditTeamPage /> },
          { path: "teams/:id", element: <EditTeamPage /> },
          { path: "hotels-repository", element: <ListHotelsRepositoryPage /> },
          {
            path: "hotels-repository/new",
            element: <EditHotelRepositoryPage />,
          },
          {
            path: "hotels-repository/:id",
            element: <EditHotelRepositoryPage />,
          },
          {
            path: "activities-repository",
            element: <ListActivitiesRepositoryPage />,
          },
          {
            path: "activities-repository/new",
            element: <EditActivityRepositoryPage />,
          },
          {
            path: "activities-repository/:id",
            element: <EditActivityRepositoryPage />,
          },
          {
            path: "transports-repository",
            element: <ListTransportsRepositoryPage />,
          },
          {
            path: "transports-repository/new",
            element: <EditTransportRepositoryPage />,
          },
          {
            path: "transports-repository/:id",
            element: <EditTransportRepositoryPage />,
          },
          {
            path: "whatsapp",
            element: <WhatsAppAccountsPage />,
          },
          {
            path: "mailboxes",
            element: <ListMailboxesPage />,
          },
          {
            path: "mailboxes/new",
            element: <EditMailboxesPage />,
          },
          {
            path: "mailboxes/:id",
            element: <EditMailboxesPage />,
          },
          {
            path: "query-sources",
            element: <ListQuerySourcesPage />,
          },
          {
            path: "query-source/new",
            element: <EditQuerySourcePage />,
          },
          {
            path: "query-source/:id",
            element: <EditQuerySourcePage />,
          },
          {
            path: "suppliers",
            element: <ListSuppliersPage />,
          },
          {
            path: "supplier/new",
            element: <EditSupplierPage />,
          },
          {
            path: "supplier/:id",
            element: <EditSupplierPage />,
          },
        ],
      },
      { path: "*", element: <Navigate to="/admin/dashboard" replace /> },
    ],
  },
]);

const loggedOutRouter = createBrowserRouter([
  {
    path: "/login",
    errorElement: routeErrorElement,
    element: (
      <Suspense
        fallback={
          <div className="h-[50vh] flex justify-center items-center">
            <Spinner />
          </div>
        }
      >
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: "/",
    errorElement: routeErrorElement,
    element: <LoggedOutLayout />,
    children: [
      { index: true, element: <LandingPage /> },
      { path: "/about", element: <AboutPage /> },
      { path: "/features", element: <FeaturesPage /> },
      { path: "/product-tour", element: <ProductTourPage /> },
      { path: "/security", element: <SecurityTrustPage /> },
      { path: "/privacy-policy", element: <PrivacyPolicyPage /> },
      { path: "/terms-conditions", element: <TermsConditionsPage /> },
    ],
  },
  { path: "*", element: <NoMatch />, errorElement: routeErrorElement },
]);

export default function App() {
  useAxiosInterceptor();
  const { message } = AntApp.useApp();
  const token = useAuthStore((state: AuthStoreType) => state.token);
  const role = useAuthStore((state: AuthStoreType) => state.user?.role);
  const authSnapshot = useAuthStore((state: AuthStoreType) => state.authSnapshot);
  const { branding } = useCrmBranding();
  const { isError, error, isPending } = useMyUser(token);

  useEffect(() => {
    if (isError) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        return;
      }
      console.error(error);
      message.error(error.message);
    }
  }, [isError, error, message]);

  if (branding && token && isPending) return <div className="p-12 text-center" role="status">Loading workspace…</div>;
  if (branding && token && isError) return <Result status="error" title="Session unavailable"
    subTitle="Sign in again to access this workspace." extra={<Button onClick={() => { useAuthStore.getState().reset(); window.location.assign('/login'); }}>Sign in</Button>} />;
  return (
    <main>
      <RouterProvider
        router={token ? (role === "super_admin" && !authSnapshot ? superAdminRouter : loggedInRouter) : loggedOutRouter}
      />
    </main>
  );
}
